// src/lib/ideaText.ts
//
// Texte d'une idée envoyée, lu par le portail, l'admin et les e-mails à l'équipe (sans code
// serveur, pour que le portail puisse l'importer). Trois formes coexistent, sans migration :
// - le parcours actuel envoie deux textes libres, chacun ramené à un paragraphe, en deux
//   paragraphes étiquetés séparés par une ligne vide : « Avant l'app :\n…\n\nAvec l'app :\n… ».
//   Les étiquettes rendent la forme sans ambiguïté (un texte libre ancien peut contenir une ligne
//   vide) et le texte reste lisible tel quel ;
// - le parcours précédent collait deux phrases : « Avant, … Depuis que j'utilise l'app Basalt
//   Lab, … » ;
// - une idée écrite avant ces parcours (texte libre) reste en un seul bloc « Idée ».
// La première phrase (`hook`) n'est plus demandée : vide pour une nouvelle idée, une idée ancienne
// garde la sienne et l'équipe la voit toujours. L'idée se présente en blocs étiquetés, dans
// l'ordre où la personne les a remplis.

/** Débuts des deux phrases du parcours précédent (idées à l'ancien format). */
export const BEFORE_LEAD = 'Avant,'
export const AFTER_LEAD = "Depuis que j'utilise l'app Basalt Lab,"

/** Étiquettes des deux textes du parcours actuel, dans le texte enregistré comme à l'affichage. */
export const BEFORE_APP_LABEL = "Avant l'app"
export const WITH_APP_LABEL = "Avec l'app"

const PARTS = /^Avant l['’]app[ \u00a0]?:[ \t]*\n([\s\S]*?)\n[ \t]*\n[ \t]*Avec l['’]app[ \u00a0]?:[ \t]*\n([\s\S]*)$/u

/** Un des deux textes : retours à la ligne et espaces en trop ramenés à une espace. */
export function cleanPart(text: string): string {
  return text.replace(/\s+/gu, ' ').trim()
}

/** Texte envoyé au serveur : les deux textes en deux paragraphes étiquetés. */
export function joinIdea(before: string, after: string): string {
  return `${BEFORE_APP_LABEL} :\n${cleanPart(before)}\n\n${WITH_APP_LABEL} :\n${cleanPart(after)}`
}

/** Place prise par les étiquettes dans le texte enregistré (joinIdea autour de deux textes vides). */
export const JOIN_OVERHEAD = joinIdea('', '').length

export type IdeaShape =
  | { kind: 'parts'; before: string; after: string }
  | { kind: 'sentences'; before: string; after: string }
  | { kind: 'free'; text: string }

/** La forme d'une idée envoyée : deux textes, deux phrases (ancien format) ou texte libre. */
export function readIdea(text: string): IdeaShape {
  const normalized = text.replace(/\r\n?/gu, '\n').trim()
  const parts = PARTS.exec(normalized)
  const before = parts?.[1]?.trim() ?? ''
  const after = parts?.[2]?.trim() ?? ''
  if (before !== '' && after !== '') return { kind: 'parts', before, after }
  const sentences = splitIdea(text)
  if (sentences.matched) return { kind: 'sentences', before: sentences.before, after: sentences.after }
  return { kind: 'free', text }
}

// « Basalt\sLab » : la règle de texte du portail ne lit que « l'app » suivi d'une espace.
const SPLIT = /^Avant,\s+([\s\S]+?)\s+Depuis que j['’]utilise l['’]app\sBasalt\sLab,\s+([\s\S]+)$/u

/** Première phrase sans les guillemets tapés par la personne : chaque page ajoute les siens. */
export function hookText(hook: string): string {
  return hook.replace(/^[\s«"“]+|[\s»"”]+$/gu, '')
}

/** Les deux phrases d'une idée envoyée ; `matched` faux quand le texte ne suit pas la forme. */
export function splitIdea(text: string): { before: string; after: string; matched: boolean } {
  const match = SPLIT.exec(text.trim())
  if (!match?.[1] || !match[2]) return { before: text, after: '', matched: false }
  return { before: match[1].trim(), after: match[2].trim(), matched: true }
}

export type IdeaPartKey = 'title' | 'beforeApp' | 'withApp' | 'before' | 'after' | 'idea' | 'hook'

/** Libellés vus par l'équipe : les mots du parcours d'idée (jamais « Accroche »). */
export const IDEA_LABELS: Record<IdeaPartKey, string> = {
  title: 'Titre de la vidéo',
  beforeApp: BEFORE_APP_LABEL,
  withApp: WITH_APP_LABEL,
  before: 'Avant…',
  after: "Depuis que j'utilise l'app Basalt Lab…",
  idea: 'Idée',
  hook: 'Première phrase (les 2 premières secondes)',
}

export type IdeaPart = { key: IdeaPartKey; label: string; text: string }

/**
 * Première phrase vue par l'équipe : les guillemets qui l'entourent tout entière s'enlèvent (la
 * fiche met les siens), jamais ceux d'une citation qui la termine (« Il m'a dit « jamais » »).
 * Une phrase faite seulement de guillemets reste telle quelle plutôt que de disparaître.
 */
function displayHook(hook: string): string {
  const text = hook.trim()
  const wrapped = /^[«"“]\s*([\s\S]*?)\s*[»"”]$/u.exec(text)
  return wrapped?.[1] ? wrapped[1] : text
}

/**
 * L'idée en blocs étiquetés : titre, « Avant l'app » et « Avec l'app » (textes tels quels), ou
 * pour l'ancien format « Avant… » et « Depuis que j'utilise l'app Basalt Lab… », puis la première
 * phrase (entre guillemets) d'une idée ancienne ; un texte libre reste en un bloc « Idée ». Un
 * bloc vide n'apparaît pas.
 */
export function ideaParts(video: { title: string; idea: string; hook: string }): IdeaPart[] {
  const shape = readIdea(video.idea)
  const hook = displayHook(video.hook)
  const story: [IdeaPartKey, string][] =
    shape.kind === 'parts'
      ? [
          ['beforeApp', shape.before],
          ['withApp', shape.after],
        ]
      : shape.kind === 'sentences'
        ? [
            ['before', shape.before],
            ['after', shape.after],
          ]
        : [['idea', shape.text.trim()]]
  const parts: [IdeaPartKey, string][] = [['title', video.title.trim()], ...story, ['hook', hook === '' ? '' : `« ${hook} »`]]
  return parts.filter(([, text]) => text !== '').map(([key, text]) => ({ key, label: IDEA_LABELS[key], text }))
}
